import React, { useState } from "react";
import FormService from "../../services/FormService";
import Swal from "sweetalert2";

const FinoCSPLanding = () => {
  const [language, setLanguage] = useState("en"); // 'en' or 'bn'
  const [formData, setFormData] = useState({
    name: "",
    shop_name: "",
    mobile: "",
    email: "",
    city: "",
    address: "",
    pincode: "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name) {
      Swal.fire("Oops!", "Please enter your full name.", "error");
      return;
    }

    if (!formData.shop_name) {
      Swal.fire("Oops!", "Please enter your shop name.", "error");
      return;
    }

    if (!formData.mobile || !/^\d{10}$/.test(formData.mobile)) {
      Swal.fire(
        "Oops!",
        "Please enter a valid 10-digit mobile number.",
        "error",
      );
      return;
    }

    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      Swal.fire("Oops!", "Please enter a valid email address.", "error");
      return;
    }

    if (!formData.city) {
      Swal.fire("Oops!", "Please enter your city/district.", "error");
      return;
    }

    if (!formData.address) {
      Swal.fire("Oops!", "Please enter your address.", "error");
      return;
    }

    if (!formData.pincode || !/^\d{6}$/.test(formData.pincode)) {
      Swal.fire("Oops!", "Please enter a valid 6-digit pincode.", "error");
      return;
    }

    setLoading(true);

    try {
      const result = await FormService.finoCspInquiry(formData);
      if (!result.success) {
        Swal.fire("Oops!", result.message, "error");
        return;
      }

      const responseData = result.data;

      if (responseData.status === "SUCCESS") {
        Swal.fire(
          "Success!",
          responseData.message || "Application submitted successfully!",
          "success",
        );

        const payment_link = responseData?.data?.payment_link;

        if (payment_link) {
          window.location.href = payment_link;
        }

        setFormData({
          name: "",
          shop_name: "",
          mobile: "",
          email: "",
          city: "",
          address: "",
          pincode: "",
        });
      } else {
        Swal.fire(
          "Oops!",
          responseData.message || "Something went wrong!",
          "error",
        );
      }
    } catch (error) {
      Swal.fire("Oops!", "Request Failed: " + (error.message || ""), "error");
    } finally {
      setLoading(false);
    }
  };

  // Content based on language
  const content = {
    en: {
      brand: "Topup Connect",
      badge: "⭐ Topup Connect | Authorized CSP Partner for West Bengal",
      title: "Open FINO Payments Bank Customer Service Point",
      fee: "One-time fee: ₹1180 only (No hidden charges)",
      subtext:
        "Convert your shop into a Mini Bank — offer full banking, gold loan, insurance, and digital payment services to every citizen.",
      stats: [
        "✅ 1000+ CSPs already working with us",
        "⏱️ Onboarding in just 2-3 days",
      ],
      cta: "Apply Now →",
      servicesTitle: "Banking & Financial Services at Your CSP",
      servicesSub:
        "Offer 20+ essential services from your outlet and earn generous commissions",
      commissionLink:
        "📄 Download Complete Commission Chart (PDF) — Check earnings on every service",
      whyTitle: "Why become a FINO CSP with Topup Connect?",
      whyList: [
        "✔️ Authorized CSP partner/provider for West Bengal",
        "✔️ 1000+ CSPs already working with us — proven model",
        "✔️ Zero monthly maintenance charges",
        "✔️ Earn up to ₹30,000+ monthly commission",
        "✔️ Full digital onboarding & support in Bengali/English/Hindi",
        "✔️ Run from a small shop, pharmacy, or Kirana store — no bank branch needed",
        "✔️ Official partnership with FINO Payments Bank (RBI regulated)",
      ],
      howTitle: "How to open your CSP?",
      howSteps: [
        "Fill enquiry form (below) — basic details",
        "Pay ₹1180 one-time fee (online — redirect after form)",
        "Complete KYC & agreement (Aadhaar + PAN)",
        "Get your CSP ID & start services within 2-3 days",
      ],
      trustBadge:
        "Trusted by 10,000+ CSPs across India | West Bengal dedicated support team",
      formTitle: "Start Your Fino Payment Bank CSP Today",
      formSub:
        "Submit your details — our team will contact you as soon as possible. You will be redirected to pay ₹1180 (one-time fee).",
      name: "Full Name",
      mobile: "Mobile Number",
      email: "Email Address",
      city: "City/District",
      shop_name: "Shop/Outlet Name",
      address: "Address",
      pincode: "Pincode",
      submit: "Submit & Proceed to Payment (₹1180)",
      tnc: "*After form submission, you will be redirected to secure payment gateway for ₹1180 CSP registration fee. Onboarding within 2-3 days post payment & KYC verification.",
      eligibilityTitle: "Eligibility Criteria",
      eligibilityList: [
        "🏪 You must have a physical shop set-up",
        "🎂 Age limit: 18 years and above",
        "📚 You must have basic financial knowledge",
        "💻 Laptop or Android mobile",
        "🖐️ Fingerprint device",
      ],
      contact: "Contact Us",
      call: "Call",
      whatsapp: "WhatsApp",
      footer:
        "© 2025 — Topup Connect | Authorized CSP Partner for FINO Payments Bank | West Bengal Circle",
      footerSupport: "Call +91 7890067005 | WhatsApp: +91 7890067005",
      footerSmall:
        "Services: Cash Withdrawal & Deposit via AePS, Instant Savings/Current Account, Fixed Deposit, Gold Loan, Micro ATM, Aadhaar Pay, Money Transfer, Insurance, Loan Referral, Bill Payments, Recharge, PAN Card, DBT, Cash Drop, CASA & more.",
    },
    bn: {
      brand: "টপআপ কানেক্ট",
      badge: "⭐ Topup Connect | পশ্চিমবঙ্গের জন্য অনুমোদিত সিএসপি পার্টনার",
      title: "ফিনো পেমেন্টস ব্যাংকের কাস্টমার সার্ভিস পয়েন্ট খুলুন",
      fee: "এককালীন ফি: মাত্র ₹১১৮০ (কোনও লুকোনো চার্জ নেই)",
      subtext:
        "আপনার স্থানীয় ব্যবসাকে শক্তিশালী করুন — প্রতিটি নাগরিককে ব্যাংকিং, গোল্ড লোন, ইন্সুরেন্স এবং ডিজিটাল পেমেন্ট পরিষেবা দিন।",
      stats: [
        "✅ ১০০০+ সিএসপি ইতিমধ্যেই আমাদের সাথে কাজ করছে",
        "⏱️ মাত্র ২-৩ দিনে অনবোর্ডিং",
      ],
      cta: "এখনই আবেদন করুন →",
      servicesTitle: "আপনার সিএসপি-তে ব্যাংকিং ও আর্থিক পরিষেবা",
      servicesSub:
        "আপনার আউটলেট থেকে ২০+ প্রয়োজনীয় পরিষেবা দিন এবং ভালো কমিশন উপার্জন করুন",
      commissionLink:
        "📄 সম্পূর্ণ কমিশন চার্ট ডাউনলোড করুন (পিডিএফ) — প্রতিটি পরিষেবায় আয় জানুন",
      whyTitle: "কেন টপআপ কানেক্টের সাথে ফিনো সিএসপি হবেন?",
      whyList: [
        "✔️ পশ্চিমবঙ্গের জন্য অনুমোদিত সিএসপি পার্টনার/প্রোভাইডার",
        "✔️ ১০০০+ সিএসপি ইতিমধ্যেই আমাদের সাথে কাজ করছে — প্রমাণিত মডেল",
        "✔️ কোনো মাসিক রক্ষণাবেক্ষণ চার্জ নেই",
        "✔️ মাসিক ₹৩০,০০০+ পর্যন্ত কমিশন উপার্জনের সুযোগ",
        "✔️ বাংলা/ইংরেজি/হিন্দিতে সম্পূর্ণ ডিজিটাল অনবোর্ডিং ও সাপোর্ট",
        "✔️ ছোট দোকান, ফার্মেসি বা কিরানা স্টোর থেকে চালান — ব্যাংক শাখার প্রয়োজন নেই",
        "✔️ ফিনো পেমেন্টস ব্যাংকের সাথে অফিসিয়াল পার্টনারশিপ (আরবিআই নিয়ন্ত্রিত)",
      ],
      howTitle: "কীভাবে আপনার সিএসপি খুলবেন?",
      howSteps: [
        "নিচের ফর্মটি পূরণ করুন — মৌলিক তথ্য",
        "₹১১৮০ এককালীন ফি অনলাইনে পরিশোধ করুন",
        "কেওয়াইসি ও চুক্তি সম্পন্ন করুন (আধার + প্যান)",
        "২-৩ দিনের মধ্যে আপনার সিএসপি আইডি পান ও পরিষেবা শুরু করুন",
      ],
      trustBadge:
        "ভারত জুড়ে ১০,০০০+ সিএসপির আস্থাভাজন | পশ্চিমবঙ্গ ডেডিকেটেড সাপোর্ট টিম",
      formTitle: "আজই আপনার সিএসপি যাত্রা শুরু করুন",
      formSub:
        "আপনার বিবরণ দিন — আমাদের দল যত তাড়াতাড়ি সম্ভব আপনার সাথে যোগাযোগ করবে। যাচাইয়ের পরে, আপনি ₹১১৮০ দিতে পেমেন্ট পেজে চলে যাবেন।",
      name: "পূর্ণ নাম",
      mobile: "মোবাইল নম্বর",
      email: "ইমেইল ঠিকানা",
      city: "জেলা/শহর",
      shop_name: "দোকানের নাম",
      address: "ঠিকানা",
      pincode: "পিনকোড",
      submit: "সাবমিট করুন ও পেমেন্টে যান (₹১১৮০)",
      tnc: "*ফর্ম জমা দেওয়ার পরে, ₹১১৮০ সিএসপি রেজিস্ট্রেশন ফির জন্য সুরক্ষিত পেমেন্ট গেটওয়েতে পুনঃনির্দেশিত হবেন। পেমেন্ট ও কেওয়াইসির পরে ২-৩ দিনের মধ্যে অনবোর্ডিং।",
      eligibilityTitle: "যোগ্যতার শর্তাবলী",
      eligibilityList: [
        "🏪 আপনার একটি ফিজিক্যাল শপ সেট-আপ থাকতে হবে",
        "🎂 বয়সসীমা: ১৮ বছর বা তার বেশি",
        "📚 আপনার মৌলিক আর্থিক জ্ঞান থাকতে হবে",
        "💻 ল্যাপটপ বা অ্যান্ড্রয়েড মোবাইল",
        "🖐️ ফিঙ্গারপ্রিন্ট ডিভাইস",
      ],
      contact: "যোগাযোগ করুন",
      call: "কল করুন",
      whatsapp: "হোয়াটসঅ্যাপ",
      footer:
        "© ২০২৫ — টপআপ কানেক্ট | ফিনো পেমেন্টস ব্যাংকের অনুমোদিত সিএসপি পার্টনার | পশ্চিমবঙ্গ সার্কেল",
      footerSupport: "কল করুন: +91 7890067005 | হোয়াটসঅ্যাপ: +91 7890067005",
      footerSmall:
        "পরিষেবা: এईপিএস-এর মাধ্যমে ক্যাশ উইথড্রোয়াল ও ডিপোজিট, ইনস্ট্যান্ট সেভিংস/কারেন্ট অ্যাকাউন্ট, ফিক্সড ডিপোজিট, গোল্ড লোন, মাইক্রো এটিএম, আধার পে, মানি ট্রান্সফার, ইন্সুরেন্স, লোন রেফারেল, বিল পেমেন্ট, রিচার্জ, পান কার্ড, ডিবিটি, ক্যাশ ড্রপ, কাসা ও আরও অনেক কিছু।",
    },
  };

  const t = content[language];

  const serviceList = [
    {
      icon: "🏦",
      titleEn: "Instant Savings Account",
      titleBn: "ইনস্ট্যান্ট সেভিংস অ্যাকাউন্ট",
      descEn: "Open zero-balance account instantly",
      descBn: "তাত্ক্ষণিকভাবে জিরো-ব্যালেন্স অ্যাকাউন্ট খুলুন",
    },
    {
      icon: "📈",
      titleEn: "Current Account",
      titleBn: "কারেন্ট অ্যাকাউন্ট",
      descEn: "For business & daily transactions",
      descBn: "ব্যবসা ও দৈনন্দিন লেনদেনের জন্য",
    },
    {
      icon: "💵",
      titleEn: "Cash Withdrawal (AePS)",
      titleBn: "ক্যাশ উইথড্রয়াল (এईপিএস)",
      descEn: "Any bank account, using aadhaar",
      descBn: "যেকোনো ব্যাংক অ্যাকাউন্ট, আধার ব্যবহার করে",
    },
    {
      icon: "💰",
      titleEn: "Cash Deposit (AePS)",
      titleBn: "ক্যাশ ডিপোজিট (এईপিএস)",
      descEn: "Deposit cash into any bank account",
      descBn: "যেকোনো ব্যাংক অ্যাকাউন্টে নগদ জমা করুন",
    },
    {
      icon: "💸",
      titleEn: "Money Transfer",
      titleBn: "মানি ট্রান্সফার",
      descEn: "Transfer money to any bank in India",
      descBn: "ভারতের যেকোনো ব্যাংকে টাকা স্থানান্তর করুন",
    },
    {
      icon: "🏧",
      titleEn: "Micro ATM",
      titleBn: "মাইক্রো এটিএম",
      descEn: "Mini banking at your doorstep",
      descBn: "আপনার দোরগোড়ায় মিনি ব্যাংকিং",
    },
    {
      icon: "🏛️",
      titleEn: "CASA (Deposit/Withdrawal)",
      titleBn: "কাসা (ডিপোজিট/উইথড্রয়াল)",
      descEn: "Savings & current account deposit/withdrawal",
      descBn: "সেভিংস ও কারেন্ট অ্যাকাউন্টে জমা/তোলা",
    },
    {
      icon: "📋",
      titleEn: "Direct Benefit Transfer (DBT)",
      titleBn: "ডাইরেক্ট বেনিফিট ট্রান্সফার (ডিবিটি)",
      descEn: "Govt subsidy enrollment at CSP points",
      descBn: "সিএসপি পয়েন্টে সরকারি ভর্তুকি নিবন্ধন",
    },
    {
      icon: "📍",
      titleEn: "Cash Drop Point (CMS)",
      titleBn: "ক্যাশ ড্রপ পয়েন্ট (সিএমএস)",
      descEn: "Secure cash drop for business payments",
      descBn: "ব্যবসায়িক পেমেন্টের জন্য নিরাপদ ক্যাশ ড্রপ",
    },
    {
      icon: "🆔",
      titleEn: "Aadhaar Pay",
      titleBn: "আধার পে",
      descEn: "Merchant payments via Aadhaar",
      descBn: "আধারের মাধ্যমে মার্চেন্ট পেমেন্ট",
    },
    {
      icon: "🥇",
      titleEn: "Gold Loan",
      titleBn: "গোল্ড লোন",
      descEn: "Instant loan against gold jewelry",
      descBn: "গয়নার বিপরীতে তাত্ক্ষণিক ঋণ",
    },
    {
      icon: "📀",
      titleEn: "Fixed Deposit",
      titleBn: "ফিক্সড ডিপোজিট",
      descEn: "High interest FD starting at ₹1000",
      descBn: "₹১০০০ থেকে উচ্চ সুদের এফডি",
    },
    {
      icon: "🛡️",
      titleEn: "Insurance",
      titleBn: "ইন্সুরেন্স",
      descEn: "Health, life & general insurance plans",
      descBn: "স্বাস্থ্য, জীবন ও সাধারণ ইন্সুরেন্স প্ল্যান",
    },
  ];

  const extraServices = [
    "Direct Benefit Transfer",
    "Cash Drop Point (CMS)",
    "CASA Deposit & Withdrawal",
    "Aadhaar Enabled Payment System (AEPS)",
    "Loan Referrals",
    "Health & Life Insurance",
    "General Insurance",
    "Domestic Money Transfer",
    "Bill Payments",
    "Mobile/DTH Recharge",
    "PAN Card Services",
  ];

  const extraServicesBn = [
    "ডাইরেক্ট বেনিফিট ট্রান্সফার",
    "ক্যাশ ড্রপ পয়েন্ট (সিএমএস)",
    "কাসা ডিপোজিট ও উইথড্রয়াল",
    "আধার ইন্যাবল্ড পেমেন্ট সিস্টেম (এईপিএস)",
    "লোন রেফারেল",
    "স্বাস্থ্য ও জীবন ইন্সুরেন্স",
    "জেনারেল ইন্সুরেন্স",
    "ডোমেস্টিক মানি ট্রান্সফার",
    "বিল পেমেন্ট",
    "মোবাইল/ডিটিএইচ রিচার্জ",
    "প্যান কার্ড পরিষেবা",
  ];

  return (
    <div className="landing">
      {/* Language Switcher with Logo in White Card */}
      <div className="language-switcher">
        <div className="container">
          <div className="logo-card">
            <img
              src="/assets/images/fino-logo.png"
              alt="FINO Payments Bank"
              className="fino-logo"
            />
          </div>
          <div className="lang-buttons">
            <button
              className={language === "en" ? "active" : ""}
              onClick={() => setLanguage("en")}
            >
              English
            </button>
            <button
              className={language === "bn" ? "active" : ""}
              onClick={() => setLanguage("bn")}
            >
              বাংলা
            </button>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <span className="badge">{t.badge}</span>
            <h1>{t.title}</h1>
            <p className="highlight">{t.fee}</p>
            <p>{t.subtext}</p>
            <div className="stats">
              {t.stats.map((stat, i) => (
                <span key={i}>{stat}</span>
              ))}
            </div>
            <a href="#enquiry" className="cta-button">
              {t.cta}
            </a>
          </div>
          <div className="hero-image">
            <div className="floating-card">
              <span>🏦 Instant Savings & Current Account</span>
              <span>💰 Cash Deposit & Withdrawal</span>
              <span>💸 Domestic Money Transfer</span>
              <span>📍 Cash Management Service (CMS)</span>
              <span>🥇 Insurance | Loan | FD</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="services">
        <div className="container">
          <h2>{t.servicesTitle}</h2>
          <p className="subhead">{t.servicesSub}</p>
          <div className="services-grid">
            {serviceList.map((service, idx) => (
              <div className="service-card" key={idx}>
                <div className="icon">{service.icon}</div>
                <h3>{language === "en" ? service.titleEn : service.titleBn}</h3>
                <p>{language === "en" ? service.descEn : service.descBn}</p>
              </div>
            ))}
          </div>
          <div className="extra-services">
            {(language === "en" ? extraServices : extraServicesBn).map(
              (service, idx) => (
                <span key={idx}>✅ {service}</span>
              ),
            )}
          </div>
          <div className="commission-link">
            <a
              href="/assets/files/fino_commission_chart.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.commissionLink}
            </a>
          </div>
        </div>
      </section>

      {/* Eligibility Criteria */}
      <section className="eligibility">
        <div className="container">
          <h2>{t.eligibilityTitle}</h2>
          <div className="eligibility-grid">
            {t.eligibilityList.map((item, idx) => (
              <div className="eligibility-card" key={idx}>
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why CSP + How it works */}
      <section className="why-csp">
        <div className="container">
          <div className="benefits">
            <h2>{t.whyTitle}</h2>
            <ul>
              {t.whyList.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </div>
          <div className="steps">
            <h2>{t.howTitle}</h2>
            <ol>
              {t.howSteps.map((step, idx) => (
                <li key={idx}>{step}</li>
              ))}
            </ol>
            <div className="trust-badge">{t.trustBadge}</div>
          </div>
        </div>
      </section>

      {/* Enquiry Form */}
      <section id="enquiry" className="enquiry">
        <div className="container">
          <h2>{t.formTitle}</h2>
          <p>{t.formSub}</p>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <input
                type="text"
                name="name"
                placeholder={t.name}
                required
                onChange={handleChange}
                value={formData.name}
              />
              <input
                type="text"
                name="shop_name"
                placeholder={t.shop_name}
                required
                onChange={handleChange}
                value={formData.shop_name}
              />
            </div>
            <div className="form-row">
              <input
                type="tel"
                name="mobile"
                placeholder={t.mobile}
                required
                onChange={handleChange}
                value={formData.mobile}
                maxLength="10"
              />
              <input
                type="email"
                name="email"
                placeholder={t.email}
                required
                onChange={handleChange}
                value={formData.email}
              />
              <input
                type="text"
                name="address"
                placeholder={t.address}
                required
                onChange={handleChange}
                value={formData.address}
              />
            </div>
            <div className="form-row">
              <input
                type="text"
                name="city"
                placeholder={t.city}
                required
                onChange={handleChange}
                value={formData.city}
              />
              <input
                type="text"
                name="pincode"
                placeholder={t.pincode}
                required
                onChange={handleChange}
                value={formData.pincode}
                maxLength="6"
              />
            </div>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? "Processing..." : t.submit}
            </button>
            <p className="tnc">{t.tnc}</p>
          </form>
        </div>
      </section>

      {/* Contact Bar */}
      <div className="contact-bar">
        <div className="container">
          <span>
            📞 {t.call}: <a href="tel:7890067005">+91 7890067005</a>
          </span>
          <span>
            💬 {t.whatsapp}:{" "}
            <a
              href="https://wa.me/917890067005"
              target="_blank"
              rel="noopener noreferrer"
            >
              +91 7890067005
            </a>
          </span>
        </div>
      </div>

      {/* Footer */}
      <footer>
        <div className="container">
          <p>{t.footer}</p>
          <p>{t.footerSupport}</p>
          <p className="small">{t.footerSmall}</p>
        </div>
      </footer>

      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
          background: #f5f9fc;
        }

        .landing {
          max-width: 1400px;
          margin: 0 auto;
          background: white;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }

        /* Language Switcher - Logo in White Card */
        .language-switcher {
          background: #022B3A;
          padding: 12px 0;
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .language-switcher .container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 15px;
        }

        .logo-card {
          display: flex;
          align-items: center;
          gap: 12px;
          background: white;
          padding: 8px 20px;
          border-radius: 50px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }

        .fino-logo {
          height: 32px;
          width: auto;
          object-fit: contain;
        }

        .lang-buttons {
          display: flex;
          gap: 12px;
        }

        .lang-buttons button {
          background: transparent;
          border: 1px solid #1F7A8C;
          color: white;
          padding: 8px 24px;
          border-radius: 30px;
          cursor: pointer;
          font-weight: 500;
          transition: 0.2s;
          font-size: 0.95rem;
        }

        .lang-buttons button.active {
          background: #FFB347;
          border-color: #FFB347;
          color: #022B3A;
        }

        .lang-buttons button:hover {
          background: #1F7A8C;
          border-color: #1F7A8C;
          color: white;
        }

        /* Hero */
        .hero {
          background: linear-gradient(135deg, #022B3A 0%, #1F7A8C 100%);
          color: white;
          padding: 60px 0 80px;
          border-radius: 0 0 40px 40px;
        }

        .hero .container {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          align-items: center;
          gap: 40px;
        }

        .hero-content {
          flex: 1;
          min-width: 280px;
        }

        .badge {
          background: rgba(255,255,255,0.2);
          padding: 8px 20px;
          border-radius: 40px;
          font-size: 0.95rem;
          display: inline-block;
          margin-bottom: 20px;
          word-break: break-word;
          white-space: normal;
          line-height: 1.4;
          max-width: 100%;
        }

        .hero-content h1 {
          font-size: 2.5rem;
          margin-bottom: 20px;
          line-height: 1.3;
          color: white;
        }

        .highlight {
          font-size: 1.5rem;
          margin: 15px 0;
        }

        .highlight strong {
          background: #FFB347;
          color: #022B3A;
          padding: 4px 12px;
          border-radius: 30px;
          display: inline-block;
        }

        .stats {
          display: flex;
          gap: 15px;
          margin: 20px 0;
          flex-wrap: wrap;
        }

        .stats span {
          background: rgba(255,255,255,0.15);
          padding: 8px 16px;
          border-radius: 40px;
          font-size: 0.95rem;
        }

        .cta-button {
          display: inline-block;
          background: #FFB347;
          color: #022B3A;
          padding: 14px 32px;
          border-radius: 50px;
          font-weight: bold;
          text-decoration: none;
          margin-top: 10px;
          font-size: 1.1rem;
          transition: transform 0.2s;
          border: none;
          cursor: pointer;
        }

        .cta-button:hover {
          transform: scale(1.02);
          background: #ffa01e;
        }

        .hero-image .floating-card {
          background: rgba(255,255,255,0.1);
          backdrop-filter: blur(10px);
          border-radius: 30px;
          padding: 25px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          font-weight: 500;
          font-size: 1rem;
          min-width: 260px;
        }

        /* Services */
        .services {
          padding: 60px 0;
          background: white;
        }

        .services h2, .why-csp h2, .enquiry h2, .eligibility h2 {
          font-size: 2rem;
          text-align: center;
          margin-bottom: 16px;
          color: #022B3A;
        }

        .subhead {
          text-align: center;
          color: #2c6979;
          margin-bottom: 40px;
          font-size: 1.1rem;
        }

        .services-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 24px;
          margin-bottom: 40px;
        }

        .service-card {
          background: #f1f9fe;
          padding: 20px 12px;
          border-radius: 24px;
          text-align: center;
          transition: 0.2s;
          border: 1px solid #e2edf2;
        }

        .service-card .icon {
          font-size: 2.2rem;
          margin-bottom: 10px;
        }

        .service-card h3 {
          font-size: 1.05rem;
          margin-bottom: 8px;
          color: #022B3A;
        }

        .service-card p {
          font-size: 0.8rem;
          color: #2c3e50;
        }

        .extra-services {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 10px;
          background: #eef7fa;
          padding: 20px;
          border-radius: 40px;
          margin: 25px 0;
        }

        .extra-services span {
          background: white;
          padding: 6px 14px;
          border-radius: 40px;
          font-size: 0.8rem;
          font-weight: 500;
          color: #022B3A;
        }

        .commission-link {
          text-align: center;
          margin-top: 25px;
          background: #fef4e8;
          padding: 14px;
          border-radius: 50px;
          font-size: 0.95rem;
        }

        .commission-link a {
          color: #022B3A;
          font-weight: bold;
          text-decoration: underline;
        }

        /* Eligibility */
        .eligibility {
          background: #e6f4f0;
          padding: 50px 0;
        }

        .eligibility-grid {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 18px;
          margin-top: 30px;
        }

        .eligibility-card {
          background: white;
          padding: 14px 24px;
          border-radius: 40px;
          font-weight: 500;
          color: #022B3A;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          font-size: 0.95rem;
        }

        /* Why CSP */
        .why-csp {
          background: #f0f4f8;
          padding: 50px 0;
        }

        .why-csp .container {
          display: flex;
          flex-wrap: wrap;
          gap: 35px;
        }

        .benefits, .steps {
          flex: 1;
          min-width: 280px;
          background: white;
          padding: 28px;
          border-radius: 28px;
          box-shadow: 0 8px 20px rgba(0,0,0,0.05);
        }

        .benefits ul, .steps ol {
          margin-top: 18px;
          padding-left: 22px;
        }

        .benefits li, .steps li {
          margin: 12px 0;
          font-size: 1rem;
        }

        .trust-badge {
          background: #022B3A;
          color: white;
          padding: 12px;
          border-radius: 35px;
          text-align: center;
          margin-top: 25px;
          font-size: 0.9rem;
        }

        /* Form */
        .enquiry {
          padding: 60px 0;
          background: white;
        }

        form {
          max-width: 750px;
          margin: 35px auto 0;
          background: #f9f9fc;
          padding: 30px;
          border-radius: 35px;
          box-shadow: 0 10px 25px rgba(0,0,0,0.05);
        }

        .form-row {
          display: flex;
          flex-wrap: wrap;
          gap: 18px;
          margin-bottom: 18px;
        }

        .form-row input {
          flex: 1;
          min-width: calc(50% - 18px);
          padding: 15px 20px;
          border: 1px solid #cbd5e1;
          border-radius: 50px;
          font-size: 1rem;
          outline: none;
          transition: border-color 0.2s;
        }

        .form-row input:focus {
          border-color: #1F7A8C;
        }

        .submit-btn {
          width: 100%;
          background: #022B3A;
          color: white;
          padding: 16px;
          border: none;
          border-radius: 50px;
          font-size: 1.1rem;
          font-weight: bold;
          cursor: pointer;
          transition: 0.2s;
        }

        .submit-btn:hover {
          background: #1F7A8C;
        }

        .submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .tnc {
          font-size: 0.7rem;
          text-align: center;
          margin-top: 18px;
          color: #5f6c7a;
        }

        /* Contact Bar */
        .contact-bar {
          background: #FFB347;
          padding: 14px 0;
          text-align: center;
        }

        .contact-bar .container {
          display: flex;
          justify-content: center;
          gap: 35px;
          flex-wrap: wrap;
        }

        .contact-bar span {
          font-weight: bold;
          color: #022B3A;
          font-size: 1rem;
        }

        .contact-bar a {
          color: #022B3A;
          text-decoration: none;
          font-weight: bold;
        }

        .contact-bar a:hover {
          text-decoration: underline;
        }

        /* Footer */
        footer {
          background: #022B3A;
          color: #cbd5e6;
          padding: 35px 0;
          text-align: center;
          font-size: 0.85rem;
        }

        footer .small {
          font-size: 0.7rem;
          margin-top: 12px;
          opacity: 0.7;
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .container {
            padding: 0 16px;
          }

          .language-switcher .container {
            flex-direction: column;
            align-items: center;
          }

          .logo-card {
            padding: 6px 16px;
          }

          .fino-logo {
            height: 28px;
          }

          .lang-buttons button {
            padding: 6px 18px;
            font-size: 0.85rem;
          }

          .hero {
            padding: 40px 0 55px;
          }

          .hero-content h1 {
            font-size: 1.8rem;
          }

          .highlight {
            font-size: 1.25rem;
          }

          .highlight strong {
            font-size: 1.3rem;
          }

          .stats {
            gap: 10px;
          }

          .stats span {
            font-size: 0.85rem;
            padding: 5px 12px;
          }

          .hero-image .floating-card {
            padding: 18px;
            font-size: 0.9rem;
            min-width: auto;
          }

          .services h2, .why-csp h2, .enquiry h2, .eligibility h2 {
            font-size: 1.7rem;
          }

          .services-grid {
            grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
            gap: 15px;
          }

          .service-card {
            padding: 15px 10px;
          }

          .service-card .icon {
            font-size: 1.8rem;
          }

          .service-card h3 {
            font-size: 0.9rem;
          }

          .service-card p {
            font-size: 0.7rem;
          }

          .extra-services {
            padding: 15px;
          }

          .extra-services span {
            font-size: 0.7rem;
            padding: 4px 12px;
          }

          .eligibility-card {
            padding: 10px 20px;
            font-size: 0.85rem;
          }

          .benefits, .steps {
            padding: 22px;
          }

          .benefits li, .steps li {
            font-size: 0.9rem;
          }

          form {
            padding: 22px;
          }

          .form-row input {
            min-width: 100%;
            padding: 13px 18px;
            font-size: 0.95rem;
          }

          .submit-btn {
            padding: 14px;
            font-size: 1rem;
          }

          .contact-bar .container {
            gap: 18px;
          }

          .contact-bar span {
            font-size: 0.9rem;
          }

          footer {
            padding: 28px 0;
          }

          footer p {
            font-size: 0.75rem;
          }

          .badge {
            font-size: 0.8rem;
            padding: 6px 14px;
          }
        }

        /* Small Mobile */
        @media (max-width: 480px) {
          .hero-content h1 {
            font-size: 1.5rem;
          }
          
          .highlight {
            font-size: 1.1rem;
          }

          .services-grid {
            grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          }

          .service-card h3 {
            font-size: 0.85rem;
          }

          .cta-button {
            padding: 12px 24px;
            font-size: 1rem;
          }

          .badge {
            font-size: 0.7rem;
            padding: 5px 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default FinoCSPLanding;
